import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Tag } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import api from "../lib/api";

const CATEGORIES = ["All", "Solar Cost", "Subsidy", "Maintenance", "Comparison"];

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blogs").then(({ data }) => setBlogs(data)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? blogs : blogs.filter(b => b.category === filter);

  return (
    <div data-testid="blog-page">
      <SEO page="blog" titleFallback="Solar Blog — Orax Solar"/>

      <section className="pt-32 pb-10 bg-[#F9F8F6]">
        <div className="container-custom">
          <div className="overline mb-3">SOLAR KNOWLEDGE HUB</div>
          <h1 className="h1 max-w-3xl">Everything about solar in Maharashtra</h1>
          <p className="text-lg text-[#2D4739] mt-4 max-w-2xl">
            Expert guides on solar cost, subsidy, maintenance, and comparisons. Written for homeowners and businesses in Akola.
          </p>
          <div className="flex flex-wrap gap-2 mt-8">
            {CATEGORIES.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  filter === c ? "bg-[#0A1F15] text-white" : "bg-white border border-[#E5E3DB] text-[#2D4739] hover:border-[#0A1F15]"
                }`}
                data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g,'-')}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 bg-[#F9F8F6]">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-20 text-[#5C7368]">Loading articles...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#5C7368]">No articles in this category yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((b, i) => (
                <motion.article
                  key={b.id}
                  initial={{opacity:0,y:20}}
                  whileInView={{opacity:1,y:0}}
                  viewport={{once:true}}
                  transition={{delay: i*0.05}}
                  className="bg-white rounded-2xl overflow-hidden border border-[#E5E3DB] card-lift"
                  data-testid={`blog-card-${b.slug}`}
                >
                  <Link to={`/blog/${b.slug}`}>
                    <div className="aspect-[16/10] overflow-hidden bg-[#F0EFEB]">
                      {b.cover_image && <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"/>}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-xs text-[#5C7368] mb-3">
                        <Tag size={12}/><span className="font-bold text-[#135934] uppercase tracking-wider">{b.category}</span>
                        {b.created_at && <><span>·</span><Calendar size={12}/>{new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric"})}</>}
                      </div>
                      <h2 className="font-display font-bold text-xl mb-2 text-[#0A1F15] group-hover:text-[#135934]">{b.title}</h2>
                      <p className="text-[#2D4739] mb-4 line-clamp-2">{b.excerpt}</p>
                      <span className="inline-flex items-center gap-1 text-[#0F52BA] font-semibold text-sm">Read article <ArrowRight weight="bold" size={14}/></span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
